<script lang="ts" setup>
import type { Category } from '~~/server/api/menu/main'
const { data } = await useLazyFetch('/api/menu/main')
const activeMenu = useState<string | null>('activeMenu', () => null)
const activeMenuTop = useState('activeMenuTop', () => 0)
const timeout = ref<number>()
const timeoutMenu = ref<number>()
const activeIndex = useState<string | null>('activeIndex', () => null)
const dropdownsOpenState = useDropdowns()
const menuRef = ref<HTMLElement>()
const touchMove = ref<boolean>(false)
const lastActiveItem = ref<HTMLElement>()

const handleClick = (e: Event, item: Category): void => {
    if (item.items.length > 0) {
        // prevent default
        e.preventDefault()
        e.stopImmediatePropagation()
        // clear timeout
        clearTimeout(timeout.value)
        // click on opened submenu?
        if (activeMenu.value === item.slug) {
            activeMenu.value = null
            activeIndex.value = null
            return
        }
        // set active submenu
        activeMenu.value = item.slug
        activeIndex.value = item.slug
        // set clicked menu as last active item to return focus after submenu closed
        lastActiveItem.value = e.target as HTMLElement
        // reset scroll position
        if (menuRef.value) {
            menuRef.value.scrollTop = 0
        }
    }
}

const handleClickOutside = (): void => {
    activeMenu.value = null
    activeIndex.value = null
    lastActiveItem.value?.focus()
}

const handleMouseEnterSubmenu = () => {
    // clear mouse leave timeout from menu link
    clearTimeout(timeoutMenu.value)
}

const handleEscKeyDown = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape') return
    handleClickOutside()
}

// Close submenu when focus leaves the submenu area (WCAG 2.4.3)
const handleFocusOut = (): void => {
    // Only handle if a submenu is open
    if (!activeMenu.value) return

    // Use setTimeout to allow the new focus target to be set
    setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement

        // Check if focus moved outside the nav element entirely
        if (menuRef.value && !menuRef.value.contains(activeElement)) {
            activeMenu.value = null
            activeIndex.value = null
            return
        }

        // Find the currently open submenu element
        const openSubmenu = menuRef.value?.querySelector('.submenu.is-active')
        if (!openSubmenu) return

        // Check if focus is still inside the open submenu
        const isFocusInSubmenu = openSubmenu.contains(activeElement)

        // Check if focus is on the trigger button for this submenu
        const triggerButton = openSubmenu.previousElementSibling
        const isFocusOnTrigger = triggerButton?.contains(activeElement)

        // If focus left both submenu and its trigger, close the submenu
        if (!isFocusInSubmenu && !isFocusOnTrigger) {
            activeMenu.value = null
            activeIndex.value = null
        }
    }, 0)
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleEscKeyDown)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleEscKeyDown)
})
</script>
<template>
  <nav
    v-if="data"
    ref="menuRef"
    class="main-menu"
    :class="dropdownsOpenState.menu ? 'is-active' : ''"
    aria-label="Hlavná navigácia"
    role="navigation"
    @focusout="handleFocusOut"
  >
    <MenuQuickLinks />
    <ul
      class="d-md-flex flex-shrink-0"
      :class="activeMenuTop ? 'submenu-opened' : ''"
    >
      <li
        v-for="(category, index) in data?.data"
        :key="`main-menu-${index}`"
        class="main-menu-item position-relative"
        :class="
          category.slug === activeIndex ? 'is-hover' : 'is-hover-out'
        "
      >
        <Tracker :data="{ title: category.title }">
          <MenuLink
            :id="`main-menu-${index}`"
            :category="category"
            :class="{
              'me-md-3 me-lg-8 me-xl-11':
                index < data.data.length - 1,
            }"
            @click="(e: Event) => handleClick(e, category)"
            @touchend="(e:Event) => {
              if(!touchMove) handleClick(e, category)
            }
            "
          />
        </Tracker>
        <LazySubmenu
          v-if="category.items.length > 0"
          :key="`submenu-${index}`"
          :class="activeMenu === category.slug ? 'is-active' : ''"
          :parent-category="category"
          :has-submenu="index === 0"
          :subcategories="category.items"
          :is-active="activeMenu === category.slug"
          :aria-labelledby="`main-menu-${index}`"
          @mouseenter="handleMouseEnterSubmenu"
        />
      </li>
    </ul>
  </nav>
</template>
<style>
@import './Menu.vars.scss';
</style>
<style lang="scss">
@import './Menu.scss';
</style>
